import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Users, Briefcase, Clock, Zap, Eye, Calendar,
  TrendingUp, X, ExternalLink, Filter, BarChart3, ChevronRight
} from 'lucide-react';

/* ─── STYLES ─────────────────────────────────────────────────── */
const S = {
  page: { padding: '14px 20px', fontFamily: "'DM Sans', sans-serif" },

  /* header */
  headerCard: {
    background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)',
    borderRadius: '14px', padding: '20px 24px 18px', marginBottom: '18px',
  },
  breadcrumb: {
    display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '11px', fontWeight: '700', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: 'var(--nd-tx25)', marginBottom: '10px',
  },
  breadcrumbActive: { color: 'rgba(34,211,165,0.55)' },
  titleRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '14px',
  },
  pageTitle: {
    fontSize: '26px', fontWeight: '800', color: 'var(--nd-tx85)',
    letterSpacing: '-0.03em', lineHeight: 1.1, margin: 0,
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  pageSub: { fontSize: '12px', color: 'var(--nd-tx30)', marginTop: '5px', fontWeight: '400' },
  teamsCountWrap: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  teamsCountNum: {
    fontFamily: "'DM Mono', monospace", fontSize: '36px', fontWeight: '700', lineHeight: 1,
    background: 'linear-gradient(90deg,#3B82F6,#22D3A5)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  teamsCountLabel: {
    fontSize: '11px', color: 'var(--nd-tx25)', fontWeight: '600',
    letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '2px',
  },
  gradLine: {
    height: '2px', borderRadius: '2px',
    background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)', marginTop: '16px',
  },

  /* stat cards */
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '14px', marginBottom: '18px',
  },
  statCard: {
    background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)',
    borderRadius: '12px', padding: '20px', transition: 'border-color .2s, transform .2s',
  },
  statIcon: {
    width: '44px', height: '44px', borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px',
  },
  statLabel: {
    fontSize: '11px', fontWeight: '700', letterSpacing: '0.10em',
    textTransform: 'uppercase', color: 'var(--nd-tx30)', marginBottom: '6px',
  },
  statValue: {
    fontSize: '30px', fontWeight: '800', lineHeight: 1,
    fontFamily: "'DM Mono', monospace",
    background: 'linear-gradient(90deg,var(--nd-text),#94A3B8)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  statUnit: {
    fontSize: '14px', fontWeight: '500', marginLeft: '4px',
    WebkitTextFillColor: 'var(--nd-tx30)', color: 'var(--nd-tx30)',
  },

  /* main table card */
  tableCard: {
    background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)',
    borderRadius: '14px', overflow: 'hidden', marginBottom: '18px',
  },
  tableHead: {
    padding: '16px 24px', borderBottom: '1px solid var(--nd-w06)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: '12px',
  },
  tableTitle: {
    fontSize: '13px', fontWeight: '700', color: 'var(--nd-tx70)',
    letterSpacing: '0.06em', textTransform: 'uppercase', margin: 0,
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  filterRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  filterBtn: (active) => ({
    padding: '7px 14px',
    background: active ? 'linear-gradient(90deg,#3B82F6,#22D3A5)' : 'var(--nd-w04)',
    border: `1px solid ${active ? 'transparent' : 'var(--nd-w08)'}`,
    borderRadius: '8px',
    color: active ? 'var(--nd-text-strong)' : 'var(--nd-tx40)',
    fontSize: '12px', fontWeight: '700', letterSpacing: '0.06em',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'all .2s',
    boxShadow: active ? '0 4px 14px rgba(34,211,165,0.20)' : 'none',
  }),
  th: {
    padding: '12px 16px', fontSize: '10px', fontWeight: '700',
    color: 'var(--nd-tx25)', textTransform: 'uppercase',
    letterSpacing: '0.12em', textAlign: 'left', whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--nd-w05)',
    background: 'var(--nd-w02)',
  },
  thCenter: {
    padding: '12px 16px', fontSize: '10px', fontWeight: '700',
    color: 'var(--nd-tx25)', textTransform: 'uppercase',
    letterSpacing: '0.12em', textAlign: 'center', whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--nd-w05)',
    background: 'var(--nd-w02)',
  },
  td: { padding: '14px 16px', fontSize: '13px', color: '#94A3B8', borderBottom: '1px solid var(--nd-w04)' },
  tdCenter: { padding: '14px 16px', fontSize: '13px', color: '#94A3B8', borderBottom: '1px solid var(--nd-w04)', textAlign: 'center' },

  /* pills */
  pillBlue: {
    background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.18)',
    color: '#60A5FA', padding: '4px 12px', borderRadius: '6px',
    fontSize: '12px', fontWeight: '700', fontFamily: "'DM Mono', monospace",
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
  pillAmber: {
    background: 'rgba(251,191,36,0.09)', border: '1px solid rgba(251,191,36,0.18)',
    color: '#FBBF24', padding: '4px 12px', borderRadius: '6px',
    fontSize: '12px', fontWeight: '700', fontFamily: "'DM Mono', monospace",
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
  pillGreen: {
    background: 'rgba(52,211,153,0.09)', border: '1px solid rgba(52,211,153,0.18)',
    color: '#34D399', padding: '4px 12px', borderRadius: '6px',
    fontSize: '12px', fontWeight: '700', fontFamily: "'DM Mono', monospace",
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
  pillGray: {
    background: 'var(--nd-w06)', border: '1px solid var(--nd-w10)',
    color: '#94A3B8', padding: '4px 12px', borderRadius: '6px',
    fontSize: '12px', fontWeight: '700', fontFamily: "'DM Mono', monospace",
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
  jobId: {
    fontFamily: "'DM Mono', monospace", fontSize: '13px', fontWeight: '700',
    background: 'linear-gradient(90deg,#3B82F6,#22D3A5)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  viewBtn: {
    padding: '6px 14px',
    background: 'linear-gradient(90deg,#3B82F6,#22D3A5)',
    color: 'var(--nd-text-strong)', border: 'none', borderRadius: '7px',
    fontWeight: '700', fontSize: '12px', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    fontFamily: "'DM Sans', sans-serif",
    boxShadow: '0 3px 12px rgba(34,211,165,0.18)',
    transition: 'opacity .2s, transform .15s',
  },

  /* loading spinner */
  spinnerWrap: { textAlign: 'center', padding: '60px 20px' },
  spinner: {
    width: '40px', height: '40px',
    border: '3px solid var(--nd-w08)',
    borderTop: '3px solid #3B82F6',
    borderRadius: '50%', animation: 'jp-spin 1s linear infinite',
    margin: '0 auto',
  },
  spinnerText: { marginTop: '14px', color: 'var(--nd-tx30)', fontSize: '13px' },

  /* empty state */
  emptyState: {
    background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)',
    borderRadius: '14px', padding: '60px 20px', textAlign: 'center', marginBottom: '18px',
  },
  emptyIcon: {
    width: '60px', height: '60px', borderRadius: '16px',
    background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
  },
  emptyTitle: { fontSize: '15px', fontWeight: '700', color: 'var(--nd-tx45)', margin: '0 0 6px' },
  emptySub:  { fontSize: '13px', color: 'var(--nd-tx22)' },

  /* modal overlay */
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.70)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10000, padding: '20px',
  },
  modal: {
    background: 'var(--nd-surface)', border: '1px solid var(--nd-w08)',
    borderRadius: '16px', maxWidth: '960px', width: '100%',
    maxHeight: '90vh', overflow: 'auto',
    boxShadow: '0 24px 64px rgba(0,0,0,0.60)',
  },
  modalHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid var(--nd-w06)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    position: 'sticky', top: 0, background: 'var(--nd-surface)', zIndex: 2,
  },
  modalTitle: {
    fontSize: '16px', fontWeight: '800', color: 'var(--nd-text)',
    display: 'flex', alignItems: 'center', gap: '10px', margin: 0,
  },
  modalTitleAccent: {
    background: 'linear-gradient(90deg,#3B82F6,#22D3A5)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  closeBtn: {
    width: '32px', height: '32px', borderRadius: '8px',
    background: 'var(--nd-w06)', border: '1px solid var(--nd-w08)',
    color: 'var(--nd-tx40)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background .2s, color .2s',
  },
  modalFooter: {
    padding: '16px 24px', borderTop: '1px solid var(--nd-w06)',
    display: 'flex', justifyContent: 'flex-end',
    position: 'sticky', bottom: 0, background: 'var(--nd-surface)',
  },
  closeBtnFooter: {
    padding: '9px 22px',
    background: 'var(--nd-w05)', border: '1px solid var(--nd-w08)',
    borderRadius: '9px', color: 'var(--nd-tx45)',
    fontWeight: '600', fontSize: '13px', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", transition: 'background .2s, color .2s',
  },
};

/* ─── AVATAR ─────────────────────────────────────────────────── */
function TeamAvatar({ name, size = 40 }) {
  const gradients = [
    ['#3B82F6','#22D3A5'], ['#8B5CF6','#3B82F6'], ['#22D3A5','#3B82F6'],
    ['#F472B6','#8B5CF6'], ['#FBBF24','#F472B6'], ['#34D399','#22D3A5'],
    ['#60A5FA','#A78BFA'], ['#F87171','#FBBF24'],
  ];
  const hash = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const [c1, c2] = gradients[hash % gradients.length];
  const initials = name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, minWidth: size, borderRadius: '10px',
      background: `linear-gradient(135deg,${c1},${c2})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--nd-text-strong)', fontWeight: '800', fontSize: size * 0.33,
      letterSpacing: '0.5px', boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {initials}
    </div>
  );
}

/* ─── STAT CARD ──────────────────────────────────────────────── */
function StatCard({ icon, label, value, unit, accentColor }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{
        ...S.statCard,
        borderColor: hov ? `${accentColor}30` : 'var(--nd-w06)',
        transform: hov ? 'translateY(-3px)' : 'none',
        boxShadow: hov ? `0 8px 28px rgba(0,0,0,0.25), 0 0 0 1px ${accentColor}20` : 'none',
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div style={{ ...S.statIcon, background: `${accentColor}12`, border: `1px solid ${accentColor}20` }}>
        {icon}
      </div>
      <div style={S.statLabel}>{label}</div>
      <div style={S.statValue}>
        {value}
        {unit && <span style={S.statUnit}>{unit}</span>}
      </div>
    </div>
  );
}

/* ─── MAIN COMPONENT ─────────────────────────────────────────── */
const AdminJobPerformance = () => {
  const [filter, setFilter]           = useState('month');
  const [summary, setSummary]         = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetails, setTeamDetails] = useState([]);
  const [showModal, setShowModal]     = useState(false);
  const [loading, setLoading]         = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/job-metrics/team-performance?filter=${filter}`
      );
      setSummary(data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    } finally { setLoading(false); }
  };

  const fetchTeamDetails = async (teamName) => {
    setModalLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/job-metrics/team-jobs?team=${teamName}&range=${filter}`
      );
      setTeamDetails(data);
      setSelectedTeam(teamName);
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching team details:', err);
    } finally { setModalLoading(false); }
  };

  useEffect(() => { fetchSummary(); }, [filter]);

  const getTotalStats = () => {
    if (!Array.isArray(summary) || !summary.length) return { totalJobs: 0, totalKw: '0.00', avgTime: '0.0' };
    return {
      totalJobs: summary.reduce((acc, t) => acc + parseInt(t.total_jobs || 0), 0),
      totalKw:   summary.reduce((acc, t) => acc + parseFloat(t.total_kw || 0), 0).toFixed(2),
      avgTime:   (summary.reduce((acc, t) => acc + parseFloat(t.avg_completion_time || 0), 0) / summary.length).toFixed(1),
    };
  };

  const stats = getTotalStats();

  const FILTERS = [
    { key: 'day',   label: 'Today' },
    { key: 'week',  label: 'Week'  },
    { key: 'month', label: 'Month' },
  ];

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
        @keyframes jp-spin { to { transform: rotate(360deg); } }
        .jp-row:hover td { background: var(--nd-w02) !important; }
        .jp-view-btn:hover { opacity: 0.82; transform: translateY(-1px); }
        .jp-close-btn:hover { background: var(--nd-w10) !important; color: var(--nd-tx70) !important; }
        .jp-filter-btn:hover { opacity: 0.85; }
        .jp-modal-close:hover { background: rgba(248,113,113,0.12) !important; color: #F87171 !important; }
      `}</style>

      {/* ── HEADER ─────────────────────────────────────── */}
      <div style={S.headerCard}>
        <div style={S.breadcrumb}>
          <span>Operations</span>
          <ChevronRight size={12} color="var(--nd-w12)"/>
          <span style={S.breadcrumbActive}>Job Performance</span>
        </div>
        <div style={S.titleRow}>
          <div>
            <div style={S.pageTitle}>
              <BarChart3 size={24} color="#3B82F6"/>
              Team Job Performance
            </div>
            <div style={S.pageSub}>Track and analyze team performance metrics</div>
          </div>
          <div style={S.teamsCountWrap}>
            <div style={S.teamsCountNum}>{summary.length}</div>
            <div style={S.teamsCountLabel}>Active Teams</div>
          </div>
        </div>
        <div style={S.gradLine}/>
      </div>

      {/* ── STAT CARDS ─────────────────────────────────── */}
      <div style={S.statsGrid}>
        <StatCard icon={<Briefcase size={22} color="#3B82F6"/>} label="Total Jobs Completed"  value={stats.totalJobs} unit=""    accentColor="#3B82F6"/>
        <StatCard icon={<Zap       size={22} color="#34D399"/>} label="Total kW Cleaned"       value={stats.totalKw}  unit="kW"  accentColor="#34D399"/>
        <StatCard icon={<Clock     size={22} color="#FBBF24"/>} label="Avg Completion Time"    value={stats.avgTime}  unit="min" accentColor="#FBBF24"/>
      </div>

      {/* ── MAIN TABLE CARD ────────────────────────────── */}
      <div style={S.tableCard}>
        <div style={S.tableHead}>
          <div style={S.tableTitle}>
            <Users size={15} color="#3B82F6"/>
            Team Performance Overview
          </div>
          <div style={S.filterRow}>
            <Filter size={14} color="var(--nd-tx25)"/>
            {FILTERS.map(f => (
              <button
                key={f.key}
                className="jp-filter-btn"
                onClick={() => setFilter(f.key)}
                style={S.filterBtn(filter === f.key)}
              >
                <Calendar size={12}/>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={S.spinnerWrap}>
            <div style={S.spinner}/>
            <p style={S.spinnerText}>Loading team performance…</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={S.th}>Team Name</th>
                  <th style={S.thCenter}>Total Jobs</th>
                  <th style={S.thCenter}>Avg. Time</th>
                  <th style={S.thCenter}>Total kW Cleaned</th>
                  <th style={{ ...S.thCenter, width: '130px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(summary) && summary.length > 0 ? (
                  summary.map((team, idx) => (
                    <tr key={idx} className="jp-row">
                      <td style={S.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <TeamAvatar name={team.team || 'Unknown'} size={42}/>
                          <span style={{ fontWeight: '700', color: 'var(--nd-text)', fontSize: '14px' }}>
                            {team.team || 'Unknown Team'}
                          </span>
                        </div>
                      </td>
                      <td style={S.tdCenter}>
                        <span style={S.pillBlue}>
                          <Briefcase size={12}/>{team.total_jobs || 0}
                        </span>
                      </td>
                      <td style={S.tdCenter}>
                        <span style={S.pillAmber}>
                          <Clock size={12}/>{team.avg_completion_time || 0} min
                        </span>
                      </td>
                      <td style={S.tdCenter}>
                        <span style={S.pillGreen}>
                          <Zap size={12}/>{team.total_kw || 0} kW
                        </span>
                      </td>
                      <td style={S.tdCenter}>
                        <button
                          className="jp-view-btn"
                          onClick={() => fetchTeamDetails(team.team)}
                          style={S.viewBtn}
                        >
                          <Eye size={13}/>View Jobs
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ padding: '60px 20px', textAlign: 'center' }}>
                      <div style={S.emptyIcon}><BarChart3 size={26} color="#3B82F6"/></div>
                      <p style={S.emptyTitle}>No performance data available</p>
                      <p style={S.emptySub}>Try selecting a different time period</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── TEAM DETAILS MODAL ─────────────────────────── */}
      {showModal && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div style={S.modal}>

            {/* Modal Header */}
            <div style={S.modalHeader}>
              <h2 style={S.modalTitle}>
                <Users size={20} color="#3B82F6"/>
                Jobs for{' '}
                <span style={S.modalTitleAccent}>{selectedTeam}</span>
              </h2>
              <button
                className="jp-modal-close"
                onClick={() => setShowModal(false)}
                style={S.closeBtn}
              >
                <X size={16}/>
              </button>
            </div>

            {/* Modal Body */}
            {modalLoading ? (
              <div style={S.spinnerWrap}>
                <div style={S.spinner}/>
                <p style={S.spinnerText}>Loading team jobs…</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={S.th}>Job ID</th>
                      <th style={S.th}>Scheduled Date</th>
                      <th style={S.thCenter}>System Size</th>
                      <th style={S.thCenter}>Duration</th>
                      <th style={S.thCenter}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamDetails.length > 0 ? (
                      teamDetails.map((job) => (
                        <tr key={job.job_id} className="jp-row">
                          <td style={S.td}>
                            <span style={S.jobId}>#{job.job_id}</span>
                          </td>
                          <td style={S.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                              <Calendar size={13} color="var(--nd-tx25)"/>
                              {new Date(job.scheduled_date).toLocaleDateString('en-GB')}
                            </div>
                          </td>
                          <td style={S.tdCenter}>
                            <span style={S.pillGreen}>
                              <Zap size={12}/>{job.system_size || 'N/A'} kW
                            </span>
                          </td>
                          <td style={S.tdCenter}>
                            <span style={S.pillAmber}>
                              <Clock size={12}/>{job.duration_minutes ?? '—'} min
                            </span>
                          </td>
                          <td style={S.tdCenter}>
                            <button
                              className="jp-view-btn"
                              onClick={() => window.open(`/admin/job-details/${job.job_id}`, '_blank')}
                              style={S.viewBtn}
                            >
                              <ExternalLink size={12}/>View
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ padding: '48px 20px', textAlign: 'center' }}>
                          <div style={S.emptyIcon}><Briefcase size={22} color="#3B82F6"/></div>
                          <p style={S.emptyTitle}>No jobs found</p>
                          <p style={S.emptySub}>This team has no jobs in the selected period</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Modal Footer */}
            <div style={S.modalFooter}>
              <button
                className="jp-close-btn"
                onClick={() => setShowModal(false)}
                style={S.closeBtnFooter}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobPerformance;